import axios from 'axios';

/** Mirrors the backend's `{ error: { code, message, details?, requestId? } }`. */
export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export interface FieldIssue {
  path: string;
  message: string;
}

/**
 * One error type for the whole app, so components never branch on axios
 * internals. Every failure — network, HTTP, or malformed response — arrives here.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.details = payload.details;
    this.requestId = payload.requestId;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED';
  }

  /** Field-level issues from a zod failure, for mapping onto form inputs. */
  get fieldIssues(): FieldIssue[] {
    if (!this.isValidation || !Array.isArray(this.details)) {
      return [];
    }

    return this.details.filter(
      (issue): issue is FieldIssue =>
        typeof issue === 'object' &&
        issue !== null &&
        typeof (issue as FieldIssue).path === 'string' &&
        typeof (issue as FieldIssue).message === 'string',
    );
  }
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const payload = (error.response?.data as { error?: ApiErrorPayload } | undefined)?.error;

    if (payload?.message) {
      return new ApiError(payload, status);
    }

    if (!error.response) {
      return new ApiError(
        { code: 'NETWORK_ERROR', message: 'Could not reach the server. Check your connection.' },
        0,
      );
    }

    return new ApiError({ code: 'UNEXPECTED_ERROR', message: error.message }, status);
  }

  return new ApiError(
    {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Something went wrong.',
    },
    0,
  );
}
